import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";
import { distinctUntilChanged, map, skip, switchMap, tap } from "rxjs/operators";
import {
	ApiProcessStage,
	AttachmentConfig,
	PerformanceProcess,
	PerformanceProcessResponse,
	ProcessActivity,
	ProcessRole,
	ProcessStage,
	ProcessSummary,
	SelectedProcess,
	StageCircleState,
	StageView,
} from "./proceso-desempeno.model";
import { MAX_ATTACHMENTS_PER_ITEM } from "./performance-attachments";
import { ApiService } from "src/app/api.service";
import { PerformanceModuleApiService } from "./performance-module-api.service";
import { InFlightRequests } from "./share-in-flight";
import { isProcessLeader, ProcessRoleFlags } from "./process-roles";
import { UsuarioServiceProvider } from "../usuario-service/usuario-service";

/**
 * Provee la data del proceso de desempeño que renderiza la vista raíz
 * ({@link ProcesoEtapasPage}): el proceso con sus etapas (hitos) y las
 * actividades de cada etapa.
 *
 * La data es mock mientras se construye el backend. Está expuesta como
 * Observable para que, al integrar la API, solo se reemplace `of(mock)` por el
 * `HttpClient.get(...)` correspondiente sin tocar la vista.
 */
@Injectable({
	providedIn: "root",
})
export class ProcesoDesempenoService {
	/** Milisegundos en un día (umbral para el estado "warning" de una etapa). */
	private static readonly ONE_DAY_MS = 24 * 60 * 60 * 1000;

	/** Clave de localStorage donde se persiste el proceso seleccionado. */
	private static readonly SELECTED_PROCESS_KEY = "currentProccesSelected";

	/**
	 * Proceso seleccionado actualmente (`{ id, name }`) o `null` si no hay
	 * ninguno. Se inicializa leyendo localStorage para que, al recargar la página
	 * estando en una vista interna del módulo, no se pierda la selección.
	 */
	private readonly currentProccesSelectedSubject = new BehaviorSubject<SelectedProcess | null>(
		this.readSelectedProcessFromStorage(),
	);

	/**
	 * Stream del proceso seleccionado. Cualquier vista del módulo puede
	 * suscribirse para reaccionar al proceso actual:
	 * `proceso$ = procesoService.currentProccesSelected$` y enlazarlo con
	 * `*ngIf="proceso$ | async as proceso"`.
	 */
	readonly currentProccesSelected$: Observable<SelectedProcess | null> =
		this.currentProccesSelectedSubject.asObservable();

	/**
	 * Cache en memoria del último detalle de proceso cargado. Evita repedir a la
	 * API el detalle (nombre, etapas y sus fechas) cuando ya se tiene en memoria;
	 * si se refresca la página el cache se pierde y se vuelve a pedir. Se limpia al
	 * cerrar sesión ({@link clearCurrentProcess}).
	 */
	private cachedProcess: PerformanceProcess | null = null;

	/**
	 * Peticiones en vuelo de {@link getProcess}, por proceso. El cache de
	 * {@link cachedProcess} solo se llena en la respuesta, así que varias lecturas del
	 * mismo tick lo encontraban vacío todas y pegaban a `processes/:id/show` una vez
	 * cada una. Ver {@link InFlightRequests}.
	 */
	private inFlight = new InFlightRequests();

	/**
	 * Actividades por código de etapa (`stage.code`). La API solo informa el
	 * progreso de la etapa (fechas, si está activa/cerrada); las tarjetas de
	 * actividad que se muestran en pantalla son catálogo del front. Una etapa con
	 * un código sin entrada aquí simplemente se renderiza sin actividades.
	 *
	 * Cada actividad declara los `roles` a los que se le muestra: las individuales
	 * se marcan `["reviewee"]`; evaluar a otros (líder, pares, reporte directo),
	 * `["reviewer"]`; y la gestión del equipo —solo quien tiene equipo, el líder—,
	 * `["leader"]` (sin `roles` = visible para cualquier participante).
	 * {@link normaliceStagesData} filtra este catálogo según los roles que el
	 * usuario cumple en el proceso ({@link resolveProcessRoles}).
	 */
	private static readonly ACTIVITIES_BY_STAGE_CODE: Record<string, ProcessActivity[]> = {
    competencies: [
			{
				// Etapa de solo consulta: el catálogo de competencias del proceso
				// (informativo, no evalúa nada aquí). Sin `roles`: lo ven el evaluado y
				// el líder, que evalúa a su equipo con esas mismas competencias.
				route: "competency-catalog",
				icon: "mdi mdi-target-account",
				title: "Competencias del proceso",
				type: "view-competencies",

				subtitle: "Consulta las competencias con las que se evalúa este proceso.",
			},
		],
		objectives: [
			{
				route: "gestion-objetivos",
				icon: "mdi mdi-target-account",
				title: "Definición de mis objetivos",
				type: "create-my-obj",

				subtitle: "Tus metas personales alineadas al equipo. Revisa el catálogo de competencias disponibles.",
				roles: ["reviewee"],
			},
      {
				route: "gestion-objetivos-equipo",
        icon: "mdi mdi-bullseye-arrow",
				title: "Definición de objetivos - mi equipo",
				type: "create-team-obj",

				subtitle: "Define los objetivos estratégicos para este periodo de evaluación. Asegúrate de que sean SMART.",
				roles: ["leader"],
			}
		],
		tracking: [
			{
				route: "gestion-objetivos/tracking",
				icon: "mdi mdi-clipboard-check-outline",
				title: "Seguimiento de mis objetivos",
				type: "review-obj",

				subtitle: "Registra tu avance y adjunta evidencia de cada objetivo.",
				roles: ["reviewee"],
			},
			{
				// Líder: monitorea el avance de los objetivos de sus evaluados. Es una
				// vista de solo lectura (no entra al plan de cada persona): las dos APIs
				// de la etapa —riesgo y resumen de ISA— solo informan.
				route: "team-tracking",
				icon: "mdi mdi-chart-timeline-variant",
				title: "Seguimiento de objetivos - mi equipo",
				type: "track-team-obj",

				subtitle: "Revisa qué objetivos de tu equipo no van a llegar a tiempo.",
				roles: ["leader"],
			}
		],
		evaluation: [
			{
				route: "gestion-objetivos/evaluation",
				icon: "mdi mdi-clipboard-check-outline",
				title: "Autoevaluación de objetivos y competencias",
				type: "review-my-obj",

				subtitle: "",
				roles: ["reviewee"],
			},
			{
				// `reviewer`, no `leader`: aquí evalúa cualquiera que evalúe a otros. El
				// líder califica objetivos y competencias; un par o un reporte directo,
				// solo competencias (una pestaña por modo en la vista del proceso).
				route: "gestion-objetivos-equipo",
				icon: "mdi mdi-clipboard-check-outline",
				title: "Evaluación de objetivos y competencias - mi equipo",
				type: "review-team-obj",

				subtitle: "",
				roles: ["reviewer"],
			},
		],
		// evaluation_competencies: [
		// 	// {
		// 	// 	route: "users-list-eval-competencies",
		// 	// 	icon: "mdi mdi-account-switch",
		// 	// 	title: "Evaluando a mis pares",
		// 	// 	type: "eval-team",

		// 	// 	subtitle: "",
		// 	// 	id: 41,
		// 	// 	status: "pending",
		// 	// 	roles: ["reviewee"],
		// 	// },
		// 	// {
		// 	// 	route: "users-list-eval-competencies",
		// 	// 	icon: "mdi mdi-account-network",
		// 	// 	title: "Evaluando a mi líder",
		// 	// 	type: "eval-leader",

		// 	// 	subtitle: "",
		// 	// 	id: 42,
		// 	// 	status: "pending",
		// 	// 	roles: ["reviewee"],
		// 	// },
		// 	{
		// 		route: "competency-self-assessment",
		// 		icon: "mdi mdi-face-recognition",
		// 		title: "Autoevaluación",
		// 		type: "autoev-comp",

		// 		subtitle: "",
		// 		id: 43,
		// 		status: "pending",
		// 		roles: ["reviewee"],
		// 	},
		// 	// {
		// 	// 	route: "users-list-eval-competencies",
		// 	// 	icon: "mdi mdi-account-group",
		// 	// 	title: "Evaluación de mi equipo",
		// 	// 	type: "eval-team-members",

		// 	// 	subtitle: "",
		// 	// 	id: 44,
		// 	// 	status: "pending",
		// 	// 	roles: ["reviewer"],
		// 	// },
		// ],
		calibration: [
			{
				// Actividad única de la etapa y **solo para el líder**: el colaborador no
				// participa de la calibración de su propio resultado. Su contenido
				// (listado → resumen → calibrar un ítem) se resuelve entero dentro de
				// ProcesoEtapasPage, sin navegar (ver INLINE_ACTIVITY_TYPES).
				route: "calibration",
				icon: "mdi mdi-scale-balance",
				title: "Calibración de equipo",
				type: "calibrate-team",

				subtitle: "Revisa y ajusta el resultado de la evaluación de tu equipo",
				roles: ["leader"],
			},
		],
		feedback: [
			{
				// Colaborador: ve SU propio feedback (reunión + resultado).
				route: "feedback/my-feedback",
				icon: "mdi mdi-comment-account",
				title: "Mi feedback",
				type: "my-feedback",

				subtitle: "Mi reunión de feedback",
				roles: ["reviewee"],
			},
			{
				// Líder: da feedback a los integrantes de su equipo.
				route: "feedback",
				icon: "mdi mdi-comment-edit",
				title: "Dar feedback",
				type: "feedback",

				subtitle: "Retroalimentación del equipo",
				roles: ["leader"],
			},
		],
		pdi: [
			{
				// Colaborador: su propio plan de desarrollo (marca su avance).
				route: "pdi-resultados/my-plan",
				icon: "mdi mdi-account",
				title: "Mi plan de desarrollo individual",
				type: "pdi-my-plan",

				subtitle: "Los objetivos de desarrollo que acordé con mi líder",
				roles: ["reviewee"],
			},
			{
				// Líder: crea y consulta el plan de cada integrante de su equipo.
				route: "pdi-resultados",
				icon: "mdi mdi-account-group",
				title: "Plan de desarrollo de mi equipo",
				type: "pdi-results",

				subtitle: "Plan de desarrollo individual de mi equipo",
				roles: ["leader"],
			},
		],
		survey: [
			{
				// Actividad única de la etapa: responder la encuesta sobre el proceso.
				// Se responde dentro de ProcesoEtapasPage (ver INLINE_ACTIVITY_TYPES);
				// esta ruta sigue siendo el deep-link a su pantalla propia.
				route: "survey",
				icon: "mdi mdi-clipboard-text-outline",
				title: "Encuesta del proceso",
				type: "process-survey",

				subtitle: "Cuéntanos tu experiencia con este proceso de desempeño.",
				// Sin `roles` a propósito: la encuesta es sobre la experiencia en el
				// proceso, así que la responde cualquier participante, sea evaluado,
				// evaluador o ambos.
			},
		],
	};

	constructor(
    private performanceModuleApiService: PerformanceModuleApiService,
    private usuarioService: UsuarioServiceProvider,
    private router: Router,
  ) {
		// Al cerrar sesión se limpia el proceso seleccionado (en memoria y storage)
		// para que la próxima sesión no herede la selección anterior. `skip(1)`
		// evita que el valor inicial del BehaviorSubject borre la selección
		// recién restaurada de localStorage al recargar la página.
		this.usuarioService.isLogged
			.pipe(skip(1), distinctUntilChanged())
			.subscribe((logged) => {
        console.log("--------logged::", logged)
				if (!logged) this.clearCurrentProcess();
			});
	}

	/** Valor actual (síncrono) del proceso seleccionado, o `null` si no hay. */
	get currentProccesSelected(): SelectedProcess | null {
		return this.currentProccesSelectedSubject.value;
	}

	/**
	 * Registra el proceso seleccionado al ingresar a un proceso desde la vista
	 * raíz del módulo. Emite el nuevo valor a los suscriptores y lo persiste en
	 * localStorage para que sobreviva a un refresco de página.
	 */
	setCurrentProcess(process: SelectedProcess): void {
		this.currentProccesSelectedSubject.next(process);
		localStorage.setItem(ProcesoDesempenoService.SELECTED_PROCESS_KEY, JSON.stringify(process));
	}

	/**
	 * Limpia el proceso seleccionado (emite `null` y borra localStorage). Se llama
	 * al volver a la vista raíz del módulo y al cerrar sesión.
	 */
	clearCurrentProcess(): void {
    console.log("--------clearCurrentProcess from storage")
		this.cachedProcess = null;
		// this.currentProccesSelectedSubject.next(null);
		// localStorage.removeItem(ProcesoDesempenoService.SELECTED_PROCESS_KEY);
	}

	/** Lee y parsea el proceso seleccionado persistido; `null` si no hay o es inválido. */
	private readSelectedProcessFromStorage(): SelectedProcess | null {
		const raw = localStorage.getItem(ProcesoDesempenoService.SELECTED_PROCESS_KEY);
		if (!raw) return null;
		try {
			return JSON.parse(raw) as SelectedProcess;
		} catch {
			return null;
		}
	}

	/**
	 * Lista los procesos de desempeño a los que el usuario tiene acceso para que
	 * seleccione a cuál ingresar (renderiza {@link ModuloDesempenoPage}).
	 */
	getProcessList(): Observable<any> {
		return this.performanceModuleApiService.getProcesses();
	}

	/**
	 * Lista de procesos del usuario ya desenvuelta (la API responde
	 * `{ data: { data: [...] } }`).
	 *
	 * Comparte la petición **en vuelo** ({@link InFlightRequests}): el selector de
	 * proceso resuelve la lista antes de decidir si abre el popover —con un solo
	 * proceso entra directo, sin overlay— y, cuando lo abre, el componente la recibe
	 * ya cargada. **No es un cache**: la clave se suelta al responder, así que el
	 * siguiente toque vuelve a pedir.
	 */
	getProcessSummaries(): Observable<ProcessSummary[]> {
		return this.inFlight.share("process-list", () =>
			this.getProcessList().pipe(map((res) => res?.data?.data ?? [])),
		);
	}

	/** Registra el proceso en contexto y entra a su detalle. */
	enterProcess(process: ProcessSummary): void {
		this.setCurrentProcess({ id: process.id, name: process.name });
		this.router.navigate([`performance/proceso/${process.id}`]);
	}

	/**
	 * Devuelve el proceso de desempeño y sus etapas (envoltura `{ data }` tal
	 * como responde la API real). **Cada lectura pega a la API**: es la fuente del
	 * estado que cambia con la interacción (completitud de las etapas), y de ahí lo
	 * leen las validaciones de negocio ({@link canEditObjectives} y hermanas).
	 *
	 * Lo único que hace {@link InFlightRequests} es **compartir la petición mientras
	 * está en vuelo** —no es un cache: la clave se suelta al responder o al fallar—.
	 * Al montar una vista del módulo suelen pedirlo tres consumidores en el mismo
	 * tick (la barra de etapas `app-process-stage-nav`, la validación de la etapa y
	 * el `getStageId` del análisis de IsA vía {@link getProcessDetail}), y salían
	 * tres GET idénticos a `processes/:id/show`. Ahora sale uno, y la siguiente
	 * lectura —volver a entrar, revalidar tras una escritura— vuelve a pedir.
	 *
	 * La respuesta también deja al día el cache de {@link getProcessDetail}: la vista
	 * del proceso lo carga con este método, y los contenidos de etapa que montan
	 * después (fecha límite, config de evidencias) leen el detalle cuando la petición
	 * ya se soltó. Con el cache vacío volvían a pedir `processes/:id/show`.
	 */
	getProcess(processId: number | string): Observable<PerformanceProcessResponse> {
		return this.inFlight.share(`process:${processId}`, () =>
			this.performanceModuleApiService.getProcess(processId).pipe(
				tap((response) => {
					if (response?.data) this.cachedProcess = response.data;
				}),
			),
		);
	}

	/**
	 * Detalle del proceso (ya desenvuelto de `{ data }`) con cache en memoria: si
	 * el mismo proceso ya se cargó en esta sesión lo devuelve sin volver a pedirlo;
	 * si el cache se perdió (refresco de página) o cambió el proceso, lo pide de
	 * nuevo a la API. Pensado para la data estable dentro de la sesión (nombre,
	 * etapas y sus fechas). Para estado que cambia con la interacción (completitud
	 * de etapas) usar {@link getProcess}, que siempre pega a la API.
	 *
	 * Con el cache vacío la lectura recae en {@link getProcess}, que **comparte la
	 * petición mientras está en vuelo**: quien pida el detalle antes de que llegue la
	 * respuesta se engancha a la misma petición en vez de abrir otra (lo normal: una
	 * vista pide la fecha límite de su etapa y la config de evidencias a la vez, y en
	 * el panel de etapas esa vista está montada dos veces). Las dos lecturas llenan
	 * el cache con **el mismo objeto**, así que asignarlo dos veces da igual.
	 */
	getProcessDetail(processId: number | string): Observable<PerformanceProcess> {
		const id = String(processId);
		if (this.cachedProcess && String(this.cachedProcess.id) === id) {
			return of(this.cachedProcess);
		}

		return this.getProcess(processId).pipe(
			map((response) => response?.data),
			tap((process) => {
				if (process) this.cachedProcess = process;
			}),
		);
	}

	/**
	 * **Plazo** (ISO) de una etapa del proceso identificada por su `code` (ej.
	 * `"tracking"`), tomada del detalle del proceso (no de un endpoint de etapa
	 * aparte): la fecha programada (`schedule_finishes_at`), y solo si no la hay, la
	 * real (`finished_at`). Es la regla inversa a la de {@link normaliceStagesData}
	 * a propósito: aquella arma el `deadline` de la vista de etapas (cuándo cerró);
	 * esta alimenta los «Plazo de entrega» (hasta cuándo se podía). Devuelve `""`
	 * si la etapa no existe o no tiene fecha.
	 */
	getStageDeadline(processId: number | string, stageCode: string): Observable<string> {
		return this.getProcessDetail(processId).pipe(
			map((process) => {
				const stage = process?.stages?.find((apiStage) => apiStage.stage?.code === stageCode);
				// El plazo es la fecha programada. `finished_at` es cuándo se cerró de
				// verdad: en una etapa cerrada fuera de calendario decía «Hasta 20 de
				// septiembre» al lado de un «22 jul. – 21 ago.».
				return stage?.schedule_finishes_at ?? stage?.finished_at ?? "";
			}),
		);
	}

	/**
	 * Cuántos avances pide la etapa de seguimiento por objetivo, o null si el
	 * backend no lo informa (la vista muestra entonces solo los registrados).
	 */
	getTrackingMinSubmissions(processId: number | string): Observable<number | null> {
		return this.getProcessDetail(processId).pipe(
			map((process) => {
				const stage = process?.stages?.find((apiStage) => apiStage.stage?.code === "tracking");
				const min = Number(stage?.min_submissions);
				return stage?.min_submissions != null && min > 0 ? min : null;
			}),
		);
	}

	/**
	 * Configuración de adjuntos del proceso (¿permite adjuntar evidencias?, máximo de
	 * archivos por ítem). El backend **no envía estas llaves** (siguen siendo
	 * provisionales), y por indicación suya se asume que adjuntar siempre está
	 * permitido; el tope por ítem cae al del backend ({@link MAX_ATTACHMENTS_PER_ITEM})
	 * y se recorta a ese máximo aunque el proceso informe uno mayor, porque la API
	 * rechazaría el envío. Qué formatos se aceptan NO depende del proceso: lo fija la
	 * API (ver `performance-attachments.ts`). Centraliza la lectura para que las
	 * vistas de evaluación no conozcan el nombre exacto de las llaves (único punto a
	 * ajustar cuando el backend las regularice). Usa el detalle cacheado del proceso
	 * ({@link getProcessDetail}).
	 */
	getAttachmentConfig(processId: number | string): Observable<AttachmentConfig> {
		return this.getProcessDetail(processId).pipe(
			map((process) => ({
				allowAttachments: process?.allow_attachments ?? true,
				maxFiles: Math.min(
					Math.max(Number(process?.max_multimedia) || MAX_ATTACHMENTS_PER_ITEM, 1),
					MAX_ATTACHMENTS_PER_ITEM,
				),
			})),
		);
	}

	/**
	 * ID de **taxonomía** de una etapa (`stage.id`, el hito de catálogo) a partir de
	 * su `code`, tomado del detalle cacheado del proceso. Es el id que piden los
	 * endpoints que acotan por etapa (ej. `stage_id` del análisis de avance de IsA),
	 * distinto del `ApiProcessStage.id` (el progreso de esa etapa en este proceso).
	 * Devuelve `null` si la etapa no existe en el proceso.
	 */
	getStageId(processId: number | string, stageCode: string): Observable<number | string | null> {
		return this.getProcessDetail(processId).pipe(
			map((process) => {
				const stage = process?.stages?.find((apiStage) => apiStage.stage?.code === stageCode);
				return stage?.stage?.id ?? null;
			}),
		);
	}

	/**
	 * Indica si la etapa de creación de objetivos (`code: "objectives"`) sigue
	 * editable para el proceso dado. Hace su propia request a la API a propósito:
	 * la validación es de negocio y debe sobrevivir a un refresco de página (la
	 * data de etapas no se persiste en memoria), por eso no depende de
	 * {@link ProcesoEtapasPage}.
	 *
	 * No editable cuando:
	 * - la etapa ya está completada (`status.code === "finished"`, criterio único
	 *   de completitud que también usa {@link normaliceStagesData}; o el legacy
	 *   `completed: true` si el backend lo enviara), o
	 * - no está completada pero su fecha de cierre (deadline) ya pasó.
	 * Si la etapa no existe/llega, se asume no editable (conservador).
	 */
	canEditObjectives(processId: number | string): Observable<boolean> {
		return this.getProcess(processId).pipe(
			map((response) => {
				const stages = response?.data?.stages ?? [];
				const objectivesStage = stages.find(
					(stage) => stage.active && stage.stage?.code === "objectives",
				);
				if (!objectivesStage) return false;
				const completed = objectivesStage.status?.code === "finished";
				if (completed) return false;
				// const deadline = objectivesStage.finished_at ?? objectivesStage.schedule_finishes_at;
				// if (deadline && new Date(deadline).getTime() < now.getTime()) return false;
				return true;
			}),
		);
	}

	/**
	 * Indica si se pueden **agregar** objetivos en el proceso: la etapa tiene que
	 * seguir editable ({@link canEditObjectives}) y el proceso no puede ser de
	 * objetivos operativos, que los define el administrador. Editar, aprobar o
	 * borrar sigue dependiendo solo de {@link canEditObjectives}.
	 */
	canCreateObjectives(processId: number | string): Observable<boolean> {
		return this.getProcess(processId).pipe(
			switchMap((response) =>
				response?.data?.operational_objectives
					? of(false)
					: this.canEditObjectives(processId),
			),
		);
	}

	/**
	 * Indica si la etapa de seguimiento (`code: "tracking"`) sigue editable para
	 * el proceso dado: misma lógica de negocio que {@link canEditObjectives} pero
	 * sobre la etapa de seguimiento. Mientras la etapa esté activa y no finalizada
	 * el colaborador puede adjuntar evidencias a sus objetivos; cuando finaliza, la
	 * vista de seguimiento pasa a solo lectura. Hace su propia request a propósito
	 * para sobrevivir a un refresco de página. Si la etapa no existe/llega, se
	 * asume no editable (conservador).
	 */
	canTrackObjectives(processId: number | string): Observable<boolean> {
		return this.getProcess(processId).pipe(
			map((response) => {
				const stages = response?.data?.stages ?? [];
				const monitoringStage = stages.find(
					(stage) => stage.active && stage.stage?.code === "tracking",
				);
				if (!monitoringStage) return false;
				const completed = monitoringStage.status?.code === "finished";
				if (completed) return false;
				return true;
			}),
		);
	}

	/**
	 * Indica si la etapa de evaluación de competencias (`code: "evaluation"`) sigue
	 * editable para el proceso dado: misma lógica de negocio que
	 * {@link canEditObjectives} pero sobre la etapa de evaluación. Mientras la etapa
	 * esté activa y no finalizada el usuario puede editar su autoevaluación ya
	 * enviada; cuando finaliza, pasa a solo lectura. Hace su propia request a
	 * propósito para sobrevivir a un refresco de página. Si la etapa no existe/llega,
	 * se asume no editable (conservador).
	 */
	canEvaluateCompetencies(processId: number | string): Observable<boolean> {
		return this.getProcess(processId).pipe(
			map((response) => {
				const stages = response?.data?.stages ?? [];
				const evaluationStage = stages.find(
					(stage) => stage.active && stage.stage?.code === "evaluation",
				);
				if (!evaluationStage) return false;
				const completed = evaluationStage.status?.code === "finished";
				if (completed) return false;
				return true;
			}),
		);
	}

	/**
	 * Convierte las etapas crudas de la API (progreso del hito de catálogo) al
	 * modelo de etapa que consume la vista. Reglas de negocio:
	 * - Solo se incluyen las etapas con `active: true`.
	 * - Solo se incluyen las etapas **controladas por el front**: aquellas cuyo
	 *   `stage.code` tiene actividades resueltas para el rol. La API puede enviar
	 *   etapas que este módulo aún no maneja (ej. `evaluation_objectives`): sin
	 *   entrada en {@link ACTIVITIES_BY_STAGE_CODE} no tienen nada que mostrar, así
	 *   que no aparecen ni en el mapa de hitos ni en el navegador rápido de etapas.
	 * - Se ordenan por `stage.position` (la API no garantiza el orden del array;
	 *   ej. "Competencias" llega al final pero tiene `position: 1`).
	 * - La fecha raw manda sobre la programada: , `started_at`/`finished_at` pisan a  `schedule_starts_at`/`schedule_finishes_at` cuando traen valor.
	 * - Las actividades no llegan en la API: se resuelven hardcodeadas por `stage.code`.
	 * - Las actividades se filtran por los roles del usuario en el proceso: quien
	 *   es evaluado (`reviewee`) ve las actividades individuales y quien evalúa
	 *   (`reviewer`) ve las de gestión/evaluación de su equipo. Un usuario puede
	 *   cumplir ambos roles y ver las dos familias de actividades. Los roles se
	 *   derivan de `is_reviewee`/`is_reviewer` con {@link resolveProcessRoles}; si
	 *   llegan vacíos el usuario no participa del proceso y no se muestra ninguna
	 *   etapa (ver {@link resolveActivitiesForRoles}).
	 */
	normaliceStagesData(apiStages: ApiProcessStage[], roles: ProcessRole[]): ProcessStage[] {
		return apiStages
			.filter((apiStage) => apiStage.active)
			.sort((a, b) => (a.stage?.position ?? 0) - (b.stage?.position ?? 0))
			.map((apiStage) => ({
				id: apiStage.id,
				code: apiStage.stage.code,
				name: apiStage.stage.name,
				startDate: apiStage.started_at ?? apiStage.schedule_starts_at ?? "",
				deadline: apiStage.finished_at ?? apiStage.schedule_finishes_at ??  "",
				completed: apiStage?.status?.code === "finished",
				activities: this.resolveActivitiesForRoles(apiStage.stage.code, roles),
			}))
			// Descarta las etapas sin actividades para mostrar (no controladas por el
			// front o sin actividades visibles para este rol).
			.filter((stage) => stage.activities.length > 0);
	}

	/**
	 * Roles que el usuario de la sesión cumple en el proceso, derivados de las
	 * banderas `is_reviewee` / `is_reviewer` / `is_leader` del detalle. Un usuario
	 * puede cumplir varios (un líder que además es evaluado) o solo uno (un par que
	 * no participa como evaluado). `leader` (tiene equipo) siempre va con `reviewer`:
	 * el líder también evalúa a otros. Sin `is_leader` se toma `is_reviewer`
	 * ({@link isProcessLeader}). **No se asume ningún rol por defecto**: si el
	 * backend no informa ninguna bandera se devuelve la lista vacía y la vista
	 * queda sin actividades, en lugar de mostrar un flujo que quizá no le
	 * corresponde al usuario.
	 */
	resolveProcessRoles(process: ProcessRoleFlags | null | undefined): ProcessRole[] {
		const roles: ProcessRole[] = [];
		const leader = isProcessLeader(process);
		if (process?.is_reviewee) roles.push("reviewee");
		if (process?.is_reviewer || leader) roles.push("reviewer");
		if (leader) roles.push("leader");
		return roles;
	}

	/**
	 * Resuelve las actividades de una etapa visibles para los roles dados: basta
	 * con que la actividad declare uno de ellos. Una actividad sin `roles` se
	 * considera visible para cualquier participante del proceso (ej. la encuesta).
	 *
	 * Sin roles no hay participación en el proceso: no se muestra **ninguna**
	 * actividad, ni siquiera las que no declaran `roles`.
	 */
	private resolveActivitiesForRoles(stageCode: string, roles: ProcessRole[]): ProcessActivity[] {
		if (!roles.length) return [];
		const activities = ProcesoDesempenoService.ACTIVITIES_BY_STAGE_CODE[stageCode] ?? [];
		return activities.filter(
			(activity) => !activity.roles || activity.roles.some((role) => roles.includes(role)),
		);
	}

	/**
	 * true si la etapa (por su `stage.code`) tiene alguna actividad visible para
	 * esos roles. Es el mismo criterio con el que {@link normaliceStagesData}
	 * descarta las etapas que el front no maneja, expuesto para que el Home
	 * ({@link PerformanceHomeService}) muestre las mismas etapas que el camino del
	 * proceso: su endpoint (`processes/latest`) manda todas las etapas del proceso,
	 * sin saber cuáles tienen algo que hacer aquí.
	 */
	hasActivitiesForRoles(stageCode: string, roles: ProcessRole[]): boolean {
		return this.resolveActivitiesForRoles(stageCode, roles).length > 0;
	}

	/**
	 * Actividades cuyo contenido se muestra **dentro** de
	 * {@link ProcesoEtapasPage} —en móvil y en desktop— cuando son la **única**
	 * actividad visible de su etapa: si la etapa solo ofrece una cosa por hacer,
	 * la tarjeta/fila intermedia solo agrega clicks (feedback de UX).
	 *
	 * Que la actividad sea única depende del **rol** del usuario en el proceso
	 * (`is_reviewee`/`is_reviewer`), porque {@link normaliceStagesData} ya filtró
	 * las actividades por rol: la misma etapa puede ir inline para un colaborador
	 * y mostrar sus dos filas para un líder que también es evaluado.
	 *
	 * Migrar una etapa = extraer el contenido de su página a un componente que
	 * reciba `processId` (ver `SurveyFormComponent` /
	 * `CompetencyCatalogListComponent`), agregar aquí su `type` y sumarlo al
	 * `ngSwitch` de {@link ProcesoEtapasPage}.
	 */
	private static readonly INLINE_ACTIVITY_TYPES = [
		"view-competencies", // catálogo de competencias (solo consulta)
		"process-survey", // encuesta del proceso
		"create-my-obj", // definición de mis objetivos
		"create-team-obj", // definición de objetivos de mi equipo (líder)
		"review-obj", // seguimiento de mis objetivos
		"track-team-obj", // seguimiento de los objetivos de mi equipo (líder)
		"review-my-obj", // autoevaluación de objetivos y competencias
		"review-team-obj", // evaluación de objetivos y competencias de mi equipo (líder)
		"my-feedback", // mi feedback (colaborador)
		"feedback", // dar feedback (líder)
		"pdi-my-plan", // mi plan de desarrollo
		"pdi-results", // plan de desarrollo de mi equipo (líder)
		"calibrate-team", // calibración del resultado de mi equipo (líder)
	];

	/** Única actividad visible de la etapa; `null` si tiene 0 o más de una. */
	soleActivity(stage: ProcessStage | StageView | null | undefined): ProcessActivity | null {
		const activities = stage?.activities ?? [];
		return activities.length === 1 ? activities[0] : null;
	}

	/**
	 * Actividad de la etapa cuyo contenido se renderiza dentro de
	 * {@link ProcesoEtapasPage}, o `null` si la etapa debe mostrar sus
	 * tarjetas/filas de actividad.
	 */
	inlineActivity(stage: ProcessStage | StageView | null | undefined): ProcessActivity | null {
		const activity = this.soleActivity(stage);
		if (!activity?.type) return null;
		return ProcesoDesempenoService.INLINE_ACTIVITY_TYPES.includes(activity.type)
			? activity
			: null;
	}

	/**
	 * true si la etapa se renderiza dentro de {@link ProcesoEtapasPage}. Tanto esa
	 * página (para mostrar el contenido en vez de las actividades) como el
	 * navegador rápido ({@link ProcessStageNavComponent}, para llevar a la página
	 * de etapas en lugar de a la ruta de la actividad) consultan este criterio,
	 * así ambos no divergen.
	 */
	rendersStageInline(stage: ProcessStage | StageView | null | undefined): boolean {
		return !!this.inlineActivity(stage);
	}

	/**
	 * Segmento de ruta (relativo a la vista del proceso) al que navega una
	 * actividad. Centraliza los casos especiales cuyo destino no coincide con
	 * `activity.route`, para que tanto {@link ProcesoEtapasPage.openActivity} como
	 * el navegador rápido de etapas resuelvan el mismo destino sin divergir.
	 */
	resolveActivitySegment(activity: ProcessActivity): string {
		if (activity.type === "create-my-obj") return "gestion-objetivos";
		if (activity.type === "review-team-obj") return "evaluacion-objetivos-equipo";
		// La autoevaluación de competencias ahora vive en la evaluación unificada
		// (objetivos + competencias) que comparte página con la revisión de objetivos.
		if (activity.type === "autoev-comp") return "gestion-objetivos/evaluation";
		return activity.route ?? "";
	}

	/**
	 * Query params con los que debe abrirse una actividad (además de su segmento).
	 * La evaluación unificada usa `section=competencies` para posicionar el stepper
	 * directamente en la primera competencia cuando se entra por la etapa de
	 * competencias (por defecto abre en el primer objetivo).
	 */
	resolveActivityQueryParams(activity: ProcessActivity): { [key: string]: string } | undefined {
		if (activity.type === "autoev-comp") return { section: "competencies" };
		return undefined;
	}

	/**
	 * Obtiene el `procesoID` recorriendo la cadena de rutas ancestras. El
	 * parámetro vive en la ruta `proceso/:procesoID` y, por la herencia
	 * `emptyOnly` de Angular, no llega al paramMap de las páginas descendientes.
	 * Si la ruta no lo expone (al cruzar varios módulos lazy + IonRouterOutlet),
	 * cae al proceso seleccionado persistido ({@link currentProccesSelected}).
	 */
	/**
	 * Etapa que el navegador rápido de etapas ({@link ProcessStageNavComponent})
	 * pidió preseleccionar al volver a {@link ProcesoEtapasPage} (one-shot). Se
	 * guarda aquí porque, al reusarse esa página desde el stack del IonRouterOutlet,
	 * `ngOnInit` no se re-ejecuta y el query param `stage` no llega de forma fiable
	 * (mismo problema que con los params de ruta ancestros, ver {@link resolveProcesoID}).
	 */
	private pendingStageId: number | string | null = null;

	/** El navegador rápido registra la etapa a preseleccionar antes de navegar. */
	requestStageSelection(stageId: number | string | null): void {
		this.pendingStageId = stageId;
	}

	/** Lee y limpia la etapa pendiente (one-shot) para que solo aplique una vez. */
	consumePendingStageId(): number | string | null {
		const id = this.pendingStageId;
		this.pendingStageId = null;
		return id;
	}

	resolveProcesoID(route: ActivatedRoute): string {
		// Fuente de verdad: la URL (deep-link / refresh sobre la vista interna).
		for (const ancestor of route.pathFromRoot) {
			const id = ancestor.snapshot.paramMap.get("procesoID");
			if (id) return id;
		}
		// Fallback robusto: el proceso seleccionado persistido en localStorage.
		// Cubre el caso en que el param del padre no llega al árbol del hijo al
		// cruzar varios módulos lazy + el IonRouterOutlet de Ionic.
		return this.currentProccesSelected?.id != null
			? String(this.currentProccesSelected.id)
			: "";
	}

	/**
	 * Enriquece las etapas con su estado de hito (círculo) y si son navegables,
	 * calculado respecto a `now`. Es una función pura: la vista la usa para
	 * pintar la barra de hitos y no necesita recalcular nada al cambiar la data.
	 */
	buildStageViewsWithCircleIndicator(stages: ProcessStage[], now: Date = new Date()): StageView[] {
		const currentStageId = this.getCurrentStageId(stages, now);
		return stages.map((stage) => {
			const circleState = this.resolveCircleState(stage, stage.id === currentStageId, now);
			// `navigable` se deriva del estado del hito para que candado y
			// navegabilidad no puedan divergir: una etapa bloqueada nunca es navegable.
			return {
				...stage,
				navigable: circleState !== "locked",
				circleState,
			};
		});
	}
	// buildStageViewsWithCircleIndicator(stages: ProcessStage[], now: Date = new Date()): StageView[] {
	// 	const currentStageId = this.getCurrentStageId(stages, now);
	// 	return stages.map((stage) => ({
	// 		...stage,
	// 		navigable: this.isReleased(stage, now),
	// 		circleState: this.resolveCircleState(stage, stage.id === currentStageId, now),
	// 	}));
	// }

	/** Cantidad de etapas completadas (para "X de Y etapas completadas"). */
	countCompleted(stages: ProcessStage[]): number {
		return stages.filter((stage) => stage.completed).length;
	}


	getCurrentStageId(stages: ProcessStage[], now: Date = new Date()): number | string | null {
    if(!stages?.length) return;
		const current = stages?.find((stage) => !stage.completed);
    if(current) {
      return current.id;
    };
    return stages[stages?.length - 1]?.id;
	}
	// getCurrentStageId(stages: ProcessStage[], now: Date = new Date()): number | string | null {
	// 	const current = stages.find((stage) => !stage.completed && this.isReleased(stage, now));
	// 	return current ? current.id : null;
	// }

	/** Una etapa está liberada cuando su fecha de inicio ya llegó. */
	private isReleased(stage: ProcessStage, now: Date): boolean {
    if(!stage.startDate) return true;
		return new Date(stage.startDate).getTime() <= now.getTime();
	}

	private resolveCircleState(
		stage: ProcessStage,
		isCurrent: boolean,
		now: Date,
	): StageCircleState {
		if (stage.completed) return "completed";
		if (isCurrent) {
			return this.isCloseToDeadline(stage, now) ? "warning" : "current";
		}
		if (!this.isReleased(stage, now)) return "locked";
		// Una etapa ya liberada y sin completar cuyo plazo venció (o vence hoy)
		// también alerta, aunque no sea la etapa actual: si no, el camino solo podría
		// marcar una etapa vencida a la vez y el resto de la deuda quedaría invisible.
		return this.isCloseToDeadline(stage, now) ? "warning" : "released";
	}
	// private resolveCircleState(
	// 	stage: ProcessStage,
	// 	isCurrent: boolean,
	// 	now: Date,
	// ): StageCircleState {
	// 	if (stage.completed) return "completed";
	// 	if (isCurrent) {
	// 		return this.isCloseToDeadline(stage, now) ? "warning" : "current";
	// 	}
	// 	return this.isReleased(stage, now) ? "released" : "locked";
	// }

	/** true si a la etapa le queda menos de 1 día para su fecha de cierre (o ya pasó). */
	private isCloseToDeadline(stage: ProcessStage, now: Date): boolean {
    if(!stage.deadline) return false;
		const remaining = new Date(stage.deadline).getTime() - now.getTime();
		return remaining <= ProcesoDesempenoService.ONE_DAY_MS;
	}
}
